import { TranslationService } from "../../../services/TranslationService";
import {
  formatCurrency,
  formatDateTimeDigits,
  formatTimeShort
} from "../../../utils/FormatUtils";
import { parseDate, restTimezone } from "../../../utils/ParseUtils";
import { getDaysSince } from "./agendaUtils";

const fill = (
  template: string,
  values: Record<string, string | number>
): string =>
  Object.entries(values).reduce(
    (text, [key, value]) => text.split(`{${key}}`).join(String(value)),
    template
  );

export const translateWith = (
  key: string,
  values: Record<string, string | number>
): string => fill(TranslationService.getTranslation(key) ?? key, values);

const capitalize = (value: string) =>
  value.charAt(0).toUpperCase() + value.slice(1);

/** Timestamps are UTC ISO without Z: apply the company timezone (project convention). */
export function toCompanyDate(iso: string): Date {
  return restTimezone(new Date(iso));
}

/** Exact date and time in the company timezone: "25/12/2026 12:00". */
export function formatAgendaDateTime(iso: string | null | undefined): string {
  return iso ? formatDateTimeDigits(toCompanyDate(iso)) : "";
}

export function formatAgendaTime(iso: string | null | undefined): string {
  return iso ? formatTimeShort(toCompanyDate(iso)) : "";
}

/** agendaDate is a calendar day (yyyy-MM-dd): no timezone applied. "miércoles 21 de octubre" */
export function formatAgendaDay(agendaDate: string | null | undefined): string {
  const date = agendaDate ? parseDate(agendaDate) : undefined;
  if (!date) {
    return "";
  }
  return capitalize(
    date
      .toLocaleDateString(TranslationService.currentLanguage, {
        weekday: "long",
        day: "numeric",
        month: "long"
      })
      .replace(",", "")
  );
}

/** fechaSugerida is a calendar day ("2026-10-31T00:00:00"): no timezone applied. "sáb 31/10" */
export function formatSuggestedDate(
  fechaSugerida: string | null | undefined
): string {
  if (!fechaSugerida) {
    return TranslationService.translate.Today;
  }
  const date = parseDate(fechaSugerida.substring(0, 10));
  if (!date) {
    return TranslationService.translate.Today;
  }
  return date
    .toLocaleDateString(TranslationService.currentLanguage, {
      weekday: "short",
      day: "2-digit",
      month: "2-digit"
    })
    .replace(",", "")
    .replace(".", "");
}

export function formatLastManagement(
  ultimaGestion: string | null | undefined,
  now: Date = new Date()
): string {
  const { translate } = TranslationService;
  if (!ultimaGestion) {
    return translate.AgendaLastManagementNone;
  }
  const days = getDaysSince(toCompanyDate(ultimaGestion), now);
  if (days === 0) {
    return translate.AgendaLastManagementToday;
  }
  if (days === 1) {
    return translate.AgendaLastManagementYesterday;
  }
  return fill(translate.AgendaLastManagementDaysAgo, { days });
}

/** Row variant: "Última gestión: hace N días", or "Sin gestiones" alone. */
export function formatLastManagementLabel(
  ultimaGestion: string | null | undefined,
  now: Date = new Date()
): string {
  const value = formatLastManagement(ultimaGestion, now);
  return ultimaGestion
    ? fill(TranslationService.translate.AgendaLastManagementLabel, { value })
    : value;
}

export const formatAgendaMoney = (value: number): string =>
  formatCurrency(value) ?? "";
